import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { Subject, forkJoin, of } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { FleetGeneralFigureService } from 'src/app/_services/fleet-general-figure.service';
import { VehicleInfoService } from 'src/app/_services/vehicle-info.service';
import { KmToMilesPipe } from 'src/app/_shared/pipes/km-to-miles.pipe';
import { RangeYPipe } from 'src/app/_shared/pipes/range-y.pipe';
import { createPlotlyChart } from 'src/app/_shared/plotlyConfig';
import { ALL_BATTERY_TYPES, BATTERY_TYPE_COLORS, BatteryTypeOption } from 'src/app/_shared/utils/battery-type';

@Component({
  selector: 'app-ev-battery-condition-chart',
  templateUrl: './ev-battery-condition-chart.component.html',
  styleUrls: ['./ev-battery-condition-chart.component.scss']
})
export class EvBatteryConditionChartComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() fleetId: string[] = [];
  @Input() selectedBatteryTypes: BatteryTypeOption[] = [];
  @Input() companyData: any;
  @Input() isMetric: boolean | undefined;
  errorMessage: boolean = false;
  dataLoading: boolean = false;
  vehicles!: any[];
  vehicleNameMap: { [key: string]: string } = {};
  vehicleBatteryTypeMap: { [key: string]: string } = {};
  private readonly kmToMilesPipe = new KmToMilesPipe();
  yValues: number[] = [];
  private readonly rangeYPipe = new RangeYPipe();

  constructor(
    private readonly vehicleInfoService: VehicleInfoService,
    private readonly fleetGeneralFigureService: FleetGeneralFigureService
  ) { }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(): void {
    this.dataLoading = true;
    this.errorMessage = false;
    
    this.fleetGeneralFigureService.getReferenceFleet().pipe(takeUntil(this.unsubscribe$)).subscribe(
      {
        next: (datapoints) => {
          const plotData = datapoints.sort((a: any, b: any) => a.distance - b.distance);
          const distance = plotData.map((dataObject: any) => dataObject.distance)
          const sohAbove = plotData.map((dataObject: any) => dataObject.sohAbove)
          const sohBelow = plotData.map((dataObject: any) => dataObject.sohBelow)

          //trace 1: bosch reference fleet -> status: Deleted

          const trace2 = {
            x: this.getConvertedValuesByUnit(distance),
            y: sohAbove,
            mode: 'lines',
            line: { shape: 'linear', color: '#006C3A' },
            name: 'Above average battery health'
          };

          const trace3 = {
            x: this.getConvertedValuesByUnit(distance),
            y: sohBelow,
            mode: 'lines',
            line: { shape: 'linear', color: '#00629A' },
            name: 'Average battery health'
          };

          const allYValues = [
            ...trace2.y,
            ...trace3.y,
          ];
          const maxY = Math.max(...allYValues);
          this.yValues = allYValues;

          const areaAbove = {
            x: [...trace2.x, ...trace2.x.slice().reverse()],
            y: [
              ...trace2.y.map(() => maxY + 10),
              ...trace2.y.slice().reverse()
            ],
            fill: 'toself',
            fillcolor: '#00884A20',
            line: { color: 'transparent' },
            showlegend: true,
            name: 'Above Average Condition',
            hoverinfo: 'skip'
          };

          const areaBetween = {
            x: [...trace2.x, ...trace3.x.slice().reverse()],
            y: [
              ...trace2.y,
              ...trace3.y.slice().reverse()
            ],
            fill: 'toself',
            fillcolor: '#007BC020',
            line: { color: 'transparent' },
            showlegend: true,
            name: 'Average Condition',
            hoverinfo: 'skip'
          };

          const areaBelow = {
            x: [...trace3.x, ...trace3.x.slice().reverse()],
            y: [
              ...trace3.y,
              ...trace3.y.map(() => 0)
            ],
            fill: 'toself',
            fillcolor: '#ED000720',
            line: { color: 'transparent' },
            showlegend: true,
            name: 'Below Average Condition',
            hoverinfo: 'skip'
          };

          this.vehicles = [];
          this.vehicleInfoService.getVehiclesWithPagination(1, 0, this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe(
            {
              next: (dataList) => {
                for (let data of dataList.data) {
                  this.vehicles.push(data);
                  this.vehicleNameMap[data.batteryIdentifier] = data.name;
                  this.vehicleBatteryTypeMap[data.batteryIdentifier] = data.batteryType;
                }
                this.fetchVehicleInfoAndUpdateChart(areaAbove, areaBetween, areaBelow, /* trace1, */ trace2, trace3)
            },
            error: (error) => {
              console.error('Error fetching Vehicles data:', error);
              this.dataLoading = false;
              this.errorMessage = true;
            },
            complete: () => {
              this.dataLoading = false;
            }
          })
        },
        error: (error) => {
          console.error('Error fetching Reference Fleet data:', error);
          this.dataLoading = false;
          this.errorMessage = true;
        },
        complete: () => {
          this.dataLoading = false;
        }
      }
    )
  }


  fetchVehicleInfoAndUpdateChart(areaAbove: any, areaBetween: any, areaBelow: any, /* trace1: any, */ trace2: any, trace3: any): void {
    const traces: { x: number[]; y: number[]; mode: string; marker: { size: number; }; name: string; }[] = [];
    const deviceSerialNumber: string[] = this.vehicles.map(vehicle => vehicle.batteryIdentifier).filter(Boolean);
    if (!deviceSerialNumber || deviceSerialNumber.length === 0) {
      this.dataLoading = false;
      return;
    }
    const batchSize = 50;
    const requests: any[] = [];
    for (let i = 0; i < deviceSerialNumber.length; i += batchSize) {
      const chunk = deviceSerialNumber.slice(i, i + batchSize);
      // Handle per-request errors so forkJoin doesn't fail on a single 404
      requests.push(
        this.vehicleInfoService.getCurrentVehicleInfo(chunk).pipe(
          catchError((err: any) => of({ __error: true, status: err?.status || err?.statusCode || 0, error: err }))
        )
      );
    }
    if (requests.length === 0) {
      this.dataLoading = false;
      return;
    }
    forkJoin(requests).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (responses: any[]) => {
        // If every response is an error and all statuses are 404, show errorMessage.
        const allErrored = responses.length > 0 && responses.every(r => r && r.__error);
        const all404 = allErrored && responses.every(r => r.status === 404);
        if (all404) {
          this.dataLoading = false;
          this.errorMessage = true;
          return;
        }

        const merged: { [key: string]: any } = {};
        responses.forEach(resp => {
          // Skip error placeholders returned by catchError
          if (resp && typeof resp === 'object') {
            if (resp.__error) {
              return;
            }
            Object.keys(resp).forEach(k => merged[k] = resp[k]);
          }
        });
        const colorContext = this.buildBatteryTypeColorContext();
        const legendGroupsShown = new Set<string>();
        Object.keys(merged).forEach((deviceSerialNumberId: string) => {
          const deviceData = merged[deviceSerialNumberId];
          const style = this.resolveVehicleStyle(this.vehicleBatteryTypeMap[deviceSerialNumberId], colorContext);
          // Vehicles whose battery type is not part of the current selection are hidden.
          if (!style) {
            return;
          }
          // Show a single legend entry per battery type group instead of one per vehicle.
          const showInLegend = !legendGroupsShown.has(style.label);
          legendGroupsShown.add(style.label);
          const mileage = deviceData.mileage;
          const trace = {
            x: this.getConvertedValuesByUnit([mileage]),
            y: [deviceData.batteryStateOfHealth],
            mode: 'markers',
            legendgroup: style.label,
            showlegend: showInLegend,
            marker: {
              color: style.color,
              size: 10
            },
            name: style.label,
          };
          traces.push(trace);
        });
        this.dataLoading = false;
        this.errorMessage = false;
        const combinedVehicleTrace = [...traces];
        this.yValues.push(...traces.map(t => t.y).flat());
        this.updateChart(areaAbove, areaBetween, areaBelow, trace2, trace3, combinedVehicleTrace);
      },
      error: (error) => {
        console.error('Error fetching Current Vehicle Info data (batched):', error);
        this.dataLoading = false;
        this.errorMessage = true;
      },
      complete: () => {
        this.dataLoading = false;
      }
    });
  }

  updateChart(areaAbove: any, areaBetween: any, areaBelow: any, trace2: any, trace3: any, trace4: any): void {
    let yRange = this.rangeYPipe.transform(this.yValues, 5);
    const layout = {
      title: 'EV Battery Condition',
      font: {
        family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
        size: 13,
      },
      xaxis: {
        title: this.getMileageUnit(),
      },
      yaxis: {
        title: 'State of Health [%]',
        range: yRange,
      },
      legend: {
        orientation: 'h',
        y: -0.3,
        xanchor: 'center',
        x: 0.5,
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
        itemclick: false,
        itemdoubleclick: false,
      },
    };
    const combinedPlotData = [areaAbove, areaBetween, areaBelow, trace2, trace3, ...trace4];
    createPlotlyChart('ev-battery-condition-chart', combinedPlotData, layout);
  }

  private buildBatteryTypeColorContext(): { specific: Map<string, { color: string; label: string }>; entireFleet: { color: string; label: string } | null } {
    const specific = new Map<string, { color: string; label: string }>();
    let entireFleet: { color: string; label: string } | null = null;
    this.selectedBatteryTypes.forEach((option, index) => {
      const color = BATTERY_TYPE_COLORS[index % BATTERY_TYPE_COLORS.length];
      if (option.batteryType === ALL_BATTERY_TYPES) {
        entireFleet = { color, label: option.label };
      } else {
        specific.set(option.batteryType, { color, label: option.label });
      }
    });
    return { specific, entireFleet };
  }

  private resolveVehicleStyle(batteryType: string | undefined, context: { specific: Map<string, { color: string; label: string }>; entireFleet: { color: string; label: string } | null }): { color: string; label: string } | null {
    // No selection at all: keep the historical behaviour and show every vehicle in black.
    if (this.selectedBatteryTypes.length === 0) {
      return { color: '#000000', label: 'Vehicles' };
    }
    const specificStyle = batteryType ? context.specific.get(batteryType) : undefined;
    if (specificStyle) {
      return specificStyle;
    }
    return context.entireFleet;
  }

  getConvertedValuesByUnit(arr: number[]): number[] {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? arr : arr.map(val => this.kmToMilesPipe.transform(val));
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? arr.map(val => this.kmToMilesPipe.transform(val))
        : arr;
    }
  }

  getMileageUnit(): string {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? 'Mileage [km]' : 'Mileage [mi]';
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? 'Mileage [mi]'
        : 'Mileage [km]';
    }
  }
}
